import type { BannerVariantType } from 'Components/shared/Banner/index.types';
import { useLayoutEffect, useRef, useState } from 'react';
import { BANNER_COLUMN_BREAKPOINT_PX } from '../index.constants';

const getVariantFromWidth = (width: number): BannerVariantType =>
  width < BANNER_COLUMN_BREAKPOINT_PX ? 'default' : 'inline';

/**
 * Observes the banner container width and returns the layout variant.
 * Only re-renders when the variant crosses the column breakpoint.
 */
const useBannerLayoutVariant = <T extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<T>(null);
  const [variant, setVariant] = useState<BannerVariantType>('inline');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const updateVariant = (width: number) => {
      const nextVariant = getVariantFromWidth(width);
      setVariant((prev) => (prev === nextVariant ? prev : nextVariant));
    };

    updateVariant(element.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      updateVariant(entry.contentRect.width);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, variant };
};

export default useBannerLayoutVariant;